import { I18n } from '@calendly/i18n';
import { Segment } from '@calendly/segment';
import { Button } from '@calendly/ui/components/button';
import { Chip, ChipGroup } from '@calendly/ui/components/chip';
import { DatePicker } from '@calendly/ui/components/date-picker';
import { Icon } from '@calendly/ui/components/icon';
import { MinimalButton } from '@calendly/ui/components/minimal-button';
import { Popover, PopoverContent, PopoverTrigger } from '@calendly/ui/components/popover';
import { BodyText } from '@calendly/ui/components/typography';
import { useDeviceType } from '@calendly/ui/hooks/use-device-type';
import classNames from 'classnames';
import moment from 'moment-timezone';
import { useCallback, useMemo, useState } from 'react';
import { MEETINGS_PRESET_IDS } from 'constants/meetings';
import { TRACKING_EVENT_NAMES } from 'hooks/analytics_events';
import { useMeetingsPresetTabsScrollBand } from './hooks/use_meetings_preset_tabs_scroll_band';
import { useMeetingsViewDate } from './hooks/use_meetings_view_date';
import { useUpcomingPreset } from './hooks/use_upcoming_preset';
import { MeetingsPhoneDisplayedCount } from './meetings_phone_displayed_count';

/**
 * Stable analytics ids for preset chips (same values historically used as `data-label`
 * for Segment Auto-Capture). Fire CTA Clicked via client Segment.track (analytics.js →
 * /v1/t) — not useTrack/eventService (/api/analytics/track), and not DOM data-label.
 * Auto-Capture on React Aria Chip/Tag can double-emit; staging often lacks Signals rules.
 */
const PRESET_CHIP_ITEMS = [{
  id: MEETINGS_PRESET_IDS.TODAY,
  labelKey: 'publisher.meetings.filter_modal.dates.today',
  dataAnalyticsId: 'meetings_page_today_filter'
}, {
  id: MEETINGS_PRESET_IDS.UPCOMING,
  labelKey: 'publisher.meetings.filter_modal.dates.upcoming',
  dataAnalyticsId: 'meetings_page_upcoming_filter'
}, {
  id: MEETINGS_PRESET_IDS.THIS_WEEK,
  labelKey: 'publisher.meetings.filter_modal.dates.this_week',
  dataAnalyticsId: 'meetings_page_this_week_filter'
}, {
  id: MEETINGS_PRESET_IDS.LAST_WEEK,
  labelKey: 'publisher.meetings.filter_modal.dates.last_week',
  dataAnalyticsId: 'meetings_page_last_week_filter'
}];
const PRESET_CTA_KEY = 'meetings_page';
const PRESET_PRODUCT_NAME = 'scheduling';
const trackPresetChipClicked = ({
  presetId,
  dataAnalyticsId
}) => {
  Segment.track(TRACKING_EVENT_NAMES.CTA_CLICKED, {
    action: 'interaction',
    cta_key: PRESET_CTA_KEY,
    cta_text: presetId,
    product_name: PRESET_PRODUCT_NAME,
    data_analytics_id: dataAnalyticsId
  });
};

/** Figma 3250:194439 — utility bar preset row: flex, 16px gaps, Day Header → divider (25px) → chips. */
const rowCss = "r9yu25i";
const rowIsPhoneCss = "rjp9pz9";
const leadingControlsCss = "l1qnkflf";

/**
 * Phone utility bar (Figma 3147:29316): padding spacing0_5x, border-bottom.
 * Date pinned; chip strip scrolls flush to the right edge.
 */
const leadingControlsIsPhoneCss = "lhytpis";

/**
 * New-shell phone: inset the date from the left only. No end padding — Figma clips the
 * preset chip strip flush to the right edge while it stays horizontally scrollable.
 */
const phoneHorizontalInsetCss = "p1cw4gq9";

/** Figma Day Header: padding 8px 0, date + chevron. */
const dayHeaderCss = "d14h7nvk";

/**
 * Fixed Day Header width (17ch date + chevron). `ddd MMM DD, YYYY` with tabular-nums is
 * length-stable for digits; 17ch covers the longest weekday/month abbr so chips never shift
 * when the selected date changes (phone and desktop).
 */
const dayHeaderFixedCss = "dxbx3yn";

/** Day Header + divider as one fixed leading block (Figma 3250:194439). */
const dateLeadingBlockCss = "d1jub4pc";
const chipsContainerCss = "c1judpff";

/** Sole phone horizontal scroller for the four preset chips — date stays pinned. */
const chipsContainerIsPhoneCss = "c8dhfbu";

/**
 * Phone Day Header (Figma 3147:29316): title-small label; width stays on dayHeaderFixedCss
 * so the chip strip width is stable while it scrolls.
 */
const dateTriggerIsPhoneCss = "d1cglagu";
const dayHeaderIsPhoneCss = "d1ckaei3";

/** Desktop: fill the fixed Day Header column so slack stays before the divider. */
const dateTriggerDesktopCss = "dlblm5i";
const chipGroupIsPhoneCss = "c9w2ij4";
const dateTriggerCss = "dlzm0pd";
const dateTriggerContentCss = "dhorzyj";
const dateTriggerContentDesktopCss = "d1vmx4z6";
const dateLabelCss = "d1mrkhy6";

/* Figma: date picker label steps down to title-small on phone widths. */
const dateLabelIsPhoneCss = "d16lc968";
const dateChevronCss = "d8n3bko";

/**
 * Desktop-only divider slot (Figma 3250:194439): 25px between Day Header and chips.
 * Phone must never render this — mobile uses the full-bleed hairline under the row.
 */
const dividerCss = "d1dsyvkv";
const meetingCountCss = "m12bwpms";
const datePickerPopoverCss = "dr8p6t2";
const datePickerFooterCss = "d1azpgzq";
const datePickerCalendarCss = "d19ry3wc";

/**
 * Preset header row shown on the meetings page when `sa_default_preset_toggle` is on.
 *
 * Left: a clickable date that opens a calendar popover. Picking a date re-centers the feed on
 * that date (past + future around it) via `useMeetingsViewDate`. The label reflects the open
 * right-rail meeting's date when one is open, otherwise stays on the picked date until scroll
 * settles, then follows the scroll anchor at the sticky-header line.
 *
 * Right: mutually exclusive date preset chips. See `useUpcomingPreset`.
 */
export const MeetingsPresetTabs = ({
  displayedMeetingCount = 0,
  /** When true (MobileHeader), use phone utility-bar layout even if isPhone is false. */
  stackedLayout = false,
  /**
   * New-shell phone edge-to-edge: parent bleed has no horizontal pad, so date/chips
   * and count rows apply spacing1x inset; hairline stays full chrome width.
   */
  edgeToEdgeInset = false
}) => {
  const {
    activePreset,
    presetFlagOn,
    togglePreset
  } = useUpcomingPreset();
  const presetTabsBandRef = useMeetingsPresetTabsScrollBand(presetFlagOn);
  const isPhone = useDeviceType('isPhone');
  // Stacked header must never show the desktop vertical divider slot.
  const usePhonePresetLayout = stackedLayout || isPhone;
  const usePhoneEdgeToEdgeInset = usePhonePresetLayout && edgeToEdgeInset;
  const {
    displayDate,
    today,
    timezone,
    selectDate
  } = useMeetingsViewDate();
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const dateLabel = useMemo(() => {
    const parsed = moment(displayDate, 'YYYY-MM-DD');
    // DD zero-pads the day so every label is the same width and preset chips do not shift.
    return (timezone ? parsed.tz(timezone, true) : parsed).format('ddd MMM DD, YYYY');
  }, [displayDate, timezone]);
  const pickerDate = displayDate;
  const handleDateChange = useCallback(date => {
    selectDate(date);
    setIsCalendarOpen(false);
  }, [selectDate]);
  const handleTodayClick = useCallback(() => {
    selectDate(today);
    setIsCalendarOpen(false);
  }, [selectDate, today]);
  const items = useMemo(() => PRESET_CHIP_ITEMS.map(item => ({
    id: item.id,
    textValue: I18n.t(item.labelKey)
  })), []);
  const analyticsIdByPresetId = useMemo(() => Object.fromEntries(PRESET_CHIP_ITEMS.map(item => [item.id, item.dataAnalyticsId])), []);
  const handleSelectionChange = useCallback(keys => {
    const selected = [...keys];
    if (selected.length === 0) {
      if (!activePreset) return;
      trackPresetChipClicked({
        presetId: activePreset,
        dataAnalyticsId: analyticsIdByPresetId[activePreset]
      });
      togglePreset(activePreset);
      return;
    }
    const nextPresetId = selected.find(id => id !== activePreset) ?? selected[selected.length - 1];
    if (nextPresetId === activePreset) return;
    trackPresetChipClicked({
      presetId: nextPresetId,
      dataAnalyticsId: analyticsIdByPresetId[nextPresetId]
    });
    togglePreset(nextPresetId);
  }, [activePreset, analyticsIdByPresetId, togglePreset]);
  if (!presetFlagOn) return null;
  const datePicker = <Popover isOpen={isCalendarOpen} onClose={() => setIsCalendarOpen(false)}>
      <PopoverTrigger>
        <Button decoration="ghost" size="small" responsive={false} className={classNames(dateTriggerCss, {
        [dateTriggerDesktopCss]: !usePhonePresetLayout,
        [dateTriggerIsPhoneCss]: usePhonePresetLayout
      })} aria-expanded={isCalendarOpen} aria-haspopup="dialog" data-testid="meetings-preset-date-trigger" onClick={() => setIsCalendarOpen(open => !open)}>
          <span className={classNames(dateTriggerContentCss, {
          [dateTriggerContentDesktopCss]: !usePhonePresetLayout
        })}>
            <span className={classNames(dateLabelCss, {
            [dateLabelIsPhoneCss]: usePhonePresetLayout
          })} data-testid="meetings-preset-date-label">
              {dateLabel}
            </span>
            <span className={dateChevronCss} aria-hidden>
              <Icon name={isCalendarOpen ? 'angle-up' : 'angle-down'} />
            </span>
          </span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className={datePickerPopoverCss} placement="bottom start">
        <div className={datePickerCalendarCss} data-testid="meetings-preset-date-picker">
          <DatePicker date={pickerDate} timezone={timezone} pastSelection onChange={handleDateChange} />
        </div>
        <div className={datePickerFooterCss}>
          <MinimalButton data-testid="meetings-preset-date-today" onClick={handleTodayClick}>
            {I18n.t('publisher.meetings.filter_modal.dates.today')}
          </MinimalButton>
        </div>
      </PopoverContent>
    </Popover>;
  const dateHeader = <div className={classNames(dayHeaderCss, dayHeaderFixedCss, {
    [dayHeaderIsPhoneCss]: usePhonePresetLayout
  })}>
      {datePicker}
    </div>;
  return <div ref={presetTabsBandRef} className={classNames(rowCss, {
    [rowIsPhoneCss]: usePhonePresetLayout
  })} data-mobile-layout={usePhonePresetLayout ? 'true' : undefined} data-testid="meetings-preset-tabs" data-column-layout="flex">
      <div className={classNames(leadingControlsCss, {
      [leadingControlsIsPhoneCss]: usePhonePresetLayout,
      [phoneHorizontalInsetCss]: usePhoneEdgeToEdgeInset
    })} data-testid="meetings-preset-leading-controls" data-phone-inline-date-presets={usePhonePresetLayout ? 'true' : undefined}>
        {usePhonePresetLayout ? dateHeader : <div className={dateLeadingBlockCss} data-testid="meetings-preset-date-leading">
            {dateHeader}
            <span className={dividerCss} aria-hidden="true" data-testid="meetings-preset-divider" />
          </div>}
        <div className={classNames(chipsContainerCss, {
        [chipsContainerIsPhoneCss]: usePhonePresetLayout
      })} data-testid="meetings-preset-columns">
          <div className={classNames({
          [chipGroupIsPhoneCss]: usePhonePresetLayout
        })}>
            <ChipGroup aria-label={I18n.t('publisher.meetings.filter_modal.dates.label')} dependencies={['selectable']} items={items} onSelectionChange={handleSelectionChange} selectedKeys={activePreset ? [activePreset] : []} selectionMode="single" selectionBehavior="replace">
              {item => <Chip key={item.id} aria-label={item.textValue} id={item.id} size="small" variant="selectable">
                  {item.textValue}
                </Chip>}
            </ChipGroup>
          </div>
        </div>
      </div>
      {usePhonePresetLayout ? <MeetingsPhoneDisplayedCount count={displayedMeetingCount} /> : <BodyText as="span" color="muted" className={meetingCountCss} data-testid="meetings-displayed-count">
          {I18n.t('publisher.meetings.preset_tabs.displaying_meetings', {
        count: displayedMeetingCount
      })}
        </BodyText>}
    </div>;
};

require("./meetings_preset_tabs.wyw-in-js.css!=!../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./meetings_preset_tabs.js");